import { z } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum VpnMessageType {
  VPN_CONNECT_REQUEST = 'VPN_CONNECT_REQUEST',
  VPN_CONNECT_RESPONSE = 'VPN_CONNECT_RESPONSE',
  VPN_ERROR = 'VPN_ERROR',
}

// Payload schemas
const vpnConnectRequestPayloadSchema = z.object({
  pingMap: z.record(z.string(), z.number()),
});

const vpnConnectResponsePayloadSchema = z.object({
  region: z.string(),
  hostname: z.string(),
  overflowed: z.boolean().optional(),
});

const vpnErrorPayloadSchema = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
});

// Full message schemas
const vpnConnectRequestSchema = baseMessageSchema
  .extend({
    type: z.literal(VpnMessageType.VPN_CONNECT_REQUEST),
  })
  .merge(vpnConnectRequestPayloadSchema);

const vpnConnectResponseSchema = baseMessageSchema
  .extend({
    type: z.literal(VpnMessageType.VPN_CONNECT_RESPONSE),
  })
  .merge(vpnConnectResponsePayloadSchema);

const vpnErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(VpnMessageType.VPN_ERROR),
  })
  .merge(vpnErrorPayloadSchema);

export const vpnMessageSchema = z.discriminatedUnion('type', [
  vpnConnectRequestSchema,
  vpnConnectResponseSchema,
  vpnErrorSchema,
]);

export type VpnMessage = z.infer<typeof vpnMessageSchema>;

type PayloadType<T extends VpnMessageType> = T extends VpnMessageType.VPN_CONNECT_REQUEST
  ? z.infer<typeof vpnConnectRequestPayloadSchema>
  : T extends VpnMessageType.VPN_CONNECT_RESPONSE
    ? z.infer<typeof vpnConnectResponsePayloadSchema>
    : T extends VpnMessageType.VPN_ERROR
      ? z.infer<typeof vpnErrorPayloadSchema>
      : never;

export function generateVpnMessage<T extends VpnMessageType>(type: T, payload: PayloadType<T>): VpnMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  } as const;
  switch (type) {
    case VpnMessageType.VPN_CONNECT_REQUEST:
      return vpnConnectRequestSchema.parse({ ...base, ...payload });
    case VpnMessageType.VPN_CONNECT_RESPONSE:
      return vpnConnectResponseSchema.parse({ ...base, ...payload });
    case VpnMessageType.VPN_ERROR:
      return vpnErrorSchema.parse({ ...base, ...payload });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled VPN message type: ${exhaustiveCheck}`);
    }
  }
}

